import React, { useState, useRef, useEffect } from 'react';
import { GlobalButton } from './GlobalButton';
import { GlobalInput } from './GlobalInput';
import { GlobalTextarea } from './GlobalTextarea';
import { useToast } from '../hooks/useToast';
import { useLocale } from '../context/DynamicLocaleProvider';
import { auth, functions, storage } from '../lib/firebase';
import { ref, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import styles from './CvCheckModal.module.css';

interface CvCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (cvCheckId: string) => void;
}

export const CvCheckModal: React.FC<CvCheckModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useLocale();
  const { showToast } = useToast();
  const [files, setFiles] = useState<File[]>([]);
  const [jobTitle, setJobTitle] = useState('');
  const [jobProfileShort, setJobProfileShort] = useState('');
  const [salaryCeilingEUR, setSalaryCeilingEUR] = useState(68000);
  const [isDragging, setIsDragging] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setFiles([]);
      setJobTitle('');
      setJobProfileShort('');
      setSalaryCeilingEUR(68000);
      setIsDragging(false);
      setIsRunning(false);
    }
  }, [isOpen]);

  const handleFileSelect = (selected: FileList | null) => {
    if (!selected?.length) return;
    const list = Array.from(selected);
    const allowed = list.filter((f) => {
      const n = f.name.toLowerCase();
      return n.endsWith('.pdf') || n.endsWith('.jpg') || n.endsWith('.jpeg') || n.endsWith('.png');
    });
    if (allowed.length !== list.length) {
      showToast(t('cv_check_modal_toast_only_pdf'), 'warning');
    }
    setFiles((prev) => [...prev, ...allowed]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const runCheck = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      showToast(t('cv_check_modal_toast_sign_in'), 'error');
      return;
    }
    if (files.length === 0) {
      showToast(t('cv_check_modal_toast_add_file'), 'error');
      return;
    }
    if (!jobTitle.trim()) {
      showToast(t('cv_check_modal_toast_job_required'), 'error');
      return;
    }

    setIsRunning(true);
    try {
      const runId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const basePath = `cv-checks/raw/${uid}/${runId}`;
      const storagePaths: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${basePath}/${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        storagePaths.push(path);
      }

      const runCvCheckFn = httpsCallable<
        { storagePaths: string[]; jobTitle: string; jobProfileShort?: string; salaryCeilingEUR?: number },
        { cvCheckId: string }
      >(functions, 'runCvCheck');
      const res = await runCvCheckFn({
        storagePaths,
        jobTitle: jobTitle.trim(),
        jobProfileShort: jobProfileShort.trim() || undefined,
        salaryCeilingEUR: salaryCeilingEUR || 68000,
      });
      const cvCheckId = res.data.cvCheckId;
      showToast(t('cv_check_modal_toast_success'), 'success');
      onClose();
      onSuccess(cvCheckId);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : t('cv_check_modal_toast_failed');
      showToast(msg, 'error');
    } finally {
      setIsRunning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>{t('cv_check_modal_title')}</h2>
          <button type="button" onClick={onClose} className={styles.closeButton} aria-label={t('cv_check_modal_close_aria')}>
            ×
          </button>
        </div>

        <div className={styles.form}>
          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>{t('cv_check_modal_files_title')}</h4>
            <div
              className={`${styles.uploadZone} ${isDragging ? styles.dragging : ''}`}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                className={styles.fileInput}
                onChange={(e) => handleFileSelect(e.target.files)}
              />
              <div className={styles.uploadContent}>
                <p className={styles.uploadHint}>
                  {t('cv_check_modal_upload_hint')}
                </p>
              </div>
            </div>
            {files.length > 0 && (
              <ul className={styles.fileList}>
                {files.map((f, i) => (
                  <li key={i} className={styles.fileItem}>
                    <span className={styles.fileName}>{f.name}</span>
                    <GlobalButton variant="secondary" destructive onClick={() => removeFile(i)}>
                      {t('cv_check_modal_remove')}
                    </GlobalButton>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>{t('cv_check_modal_context_title')}</h4>
            <div className={styles.formRow}>
              <GlobalInput
                label={t('cv_check_modal_job_title')}
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className={styles.formInput}
              />
              <GlobalInput
                label={t('cv_check_modal_salary_ceiling')}
                type="number"
                value={String(salaryCeilingEUR)}
                onChange={(e) => setSalaryCeilingEUR(Number(e.target.value) || 68000)}
                className={styles.formInput}
              />
            </div>
            <GlobalTextarea
              label={t('cv_check_modal_job_profile')}
              value={jobProfileShort}
              onChange={(e) => setJobProfileShort(e.target.value)}
              rows={4}
              className={styles.formInput}
            />
          </div>

          <div className={styles.actions}>
            <GlobalButton type="button" variant="secondary" onClick={onClose} disabled={isRunning}>
              {t('cv_check_modal_cancel')}
            </GlobalButton>
            <GlobalButton onClick={runCheck} isLoading={isRunning} disabled={isRunning}>
              {isRunning ? t('cv_check_modal_running') : t('cv_check_modal_run')}
            </GlobalButton>
          </div>
        </div>
      </div>
    </div>
  );
};
